import { useTranslation } from 'react-i18next'
import { type Route } from './+types/route'
import { Article } from '~/components/article.tsx'
import { Cols } from '~/components/cols.tsx'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { Gradient } from '~/components/gradients.tsx'
import { Image } from '~/components/image.tsx'
import { Layout } from '~/components/layout.tsx'
import { Markdown } from '~/components/markdown.tsx'
import { Separator } from '~/components/separator.tsx'
import { StructuredData } from '~/components/structured-data.tsx'
import { ErrorNotFound } from '~/components/ui/errors/error-not-found'
import { Enum_Post_Category, PublicationState } from '~/model/api.ts'
import { getFormatter } from '~/utils/date-formatter.server.ts'
import { api } from '~/utils/gql-client.server.ts'
import { markdown } from '~/utils/markdown.server.ts'
import { type MetaProps, generateMeta, validateSeo } from '~/utils/meta.ts'
import { invariantResponse } from '~/utils/misc.ts'
import { getPageState } from '~/utils/page-state.ts'

export function meta({ data }: Route.MetaArgs) {
  return data?.metaProps
    ? generateMeta(data.metaProps as MetaProps)
    : [{ title: '404 not found' }]
}

export let handle = {
  i18n: 'press',
}

export async function loader({ request, params }: Route.LoaderArgs) {
  const pageState = await getPageState(request)

  const { postBySlug } = await api.Post({
    category: Enum_Post_Category.Press,
    slug: params.slug,
    publicationState: PublicationState.Live,
    locale: pageState.locale,
  })

  invariantResponse(postBySlug, 'Press post not found', { status: 404 })

  const formatter = await getFormatter(request)

  const textDisplay = markdown(postBySlug.text)

  const metaProps: MetaProps = {
    seo: await validateSeo(postBySlug.SEO),
    pageState: pageState,
    localizations: postBySlug?.localizations?.data.map(
      (a) => a.attributes?.locale,
    ) as string[],
  }

  return {
    metaProps: metaProps,
    textDisplay,
    post: postBySlug,
    dateDisplay: postBySlug.publishedAt
      ? formatter.format(new Date(postBySlug.publishedAt))
      : null,
  }
}

export default function Press({ loaderData }: Route.ComponentProps) {
  const { post, dateDisplay, textDisplay } = loaderData

  const structuredData: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: post.title,
    image: [post?.cover?.data?.attributes?.url],
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
  }

  return (
    <Layout>
      <div className="relative overflow-hidden py-32">
        <Gradient />

        {post.cover?.data?.attributes?.url && (
          <div className="absolute top-[-130px] right-[-550px] md:top-[-150px] md:right-[-300px] lg:top-[-350px] lg:right-[-600px]">
            <Image
              src={post.cover.data.attributes.url}
              formats={post.cover.data.attributes.formats}
              alt=""
              className="hero-mask max-w-none object-cover opacity-[72%] md:h-[630px] md:w-[1200px] lg:h-[1280px] lg:w-[1920px]"
            />
          </div>
        )}

        <div className="relative z-10 container">
          <Cols size="8">
            <div className="flex flex-col gap-6">
              <p className="text-body-16 text-text-muted">{dateDisplay}</p>
              <h1 className="font-display text-display-60 leading-tight font-extrabold text-text-default uppercase italic">
                {post.title}
              </h1>
            </div>
          </Cols>
        </div>
      </div>

      <div className="container">
        <Cols size="8">
          <div className="py-24">
            <p className="text-body-20 text-text-muted">{post.perex}</p>
            <Separator className="my-12" />
            <Article>
              <Markdown content={textDisplay} />
            </Article>
          </div>
        </Cols>
      </div>
      <StructuredData structuredData={structuredData} />
    </Layout>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation(['press'])
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        404: () => <ErrorNotFound description={t('press_not_found')} />,
      }}
    />
  )
}
